"use client";

import { useState, type ReactNode } from "react";
import { MenuIcon } from "lucide-react";

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@tripcart/traveler-ui/components/sheet";

import { useTranslator } from "@/lib/translation-context";

import styles from "./hf-mobile-nav.module.css";

/**
 * The menu trigger a custom header collapses behind on phone widths
 * (feature: header-footer-builder).
 *
 * "Mobile collapses, it doesn't drop" (product.md): what does not fit the
 * visible bar is one tap away rather than gone. This island is ONLY the sheet —
 * the trigger, the panel, and the open/close state. Everything inside it is
 * server-rendered and handed in as `children`: the navigation itself, and the
 * elements `SiteHeaderMobileStack` tucked. That split is what keeps the client
 * cost to the sheet the storefront already ships in `nav.tsx`, with no element
 * renderer following it into the bundle.
 *
 * `useState` rather than an uncontrolled `Sheet`: the header's own elements
 * inside the panel are plain anchors with no `SheetClose` around them, so a tap
 * on one navigates and would leave the panel open over the new page. Closing on
 * a click that landed on an anchor handles those without asking every element
 * body to know it might be rendered in a sheet — and without closing on a tap
 * that was not a navigation, which an unconditional handler would do.
 */
export function HfMobileNav({
  children,
}: {
  children: ReactNode;
}): React.ReactElement {
  const t = useTranslator();
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        aria-label={t("Open Navigation Menu")}
        className={styles.trigger}
      >
        <MenuIcon className={styles.icon} strokeWidth={1.5} />
      </SheetTrigger>
      <SheetContent size="sm">
        <SheetHeader>
          <SheetTitle>{t("Menu")}</SheetTitle>
        </SheetHeader>
        {/* Anything in here that navigates should also dismiss — see the note
            on `useState` above. Not a control itself: it carries no role and no
            key handler because closing is a side effect of a link the keyboard
            already reaches, never an action of its own. */}
        <SheetBody
          className={styles.body}
          onClick={(event) => {
            if ((event.target as HTMLElement).closest("a")) setOpen(false);
          }}
        >
          {children}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
